<template>
  <VdPage class="sandboxes-list-page" title="Sandboxes" :subtitle="`(${sandboxes.length})`" :loading="loading">
    <template #actions>
      <VdInput v-model="searchTerm" class="search-input" placeholder="Looking for something...">
        <template #prepend>
          <VdIcon store="fa" name="search" />
        </template>
      </VdInput>
    </template>

    <keep-alive v-if="finished && filteredSandboxes.length === 0">
      <vd-empty :title="searchTerm ? 'not found' : 'no sandbox'">
        <div v-if="searchTerm">
          <p>Your search for a sandbox has no results</p>
        </div>
        <div v-else>
          <p>Sorry, there is no data.</p>
        </div>
      </vd-empty>
    </keep-alive>
    <div v-else>
      <VdCard v-for="sandbox in filteredSandboxes" :key="sandbox.id" size="small" class="sandbox-card">
        <section class="left">
          <vd-custom-icon name="sandbox-icon" />
          <div class="sandbox-main-info">
            <h3 class="sandbox-name">
              <router-link :to="previewSandbox(sandbox.id)">{{ sandbox.name }}</router-link>
            </h3>
            <span class="sandbox-details">
              <span>Classes: {{ sandbox.numberClasses }}</span>
              <span>&nbsp;-&nbsp;</span>
              <span>Attributes: {{ sandbox.totalAttributes }}</span>
            </span>
          </div>
        </section>
        <vd-dropdown v-if="canWriteSandboxes">
          <vd-dropdown-item dangerous @click="onSandboxDelete(sandbox)">Delete "{{ sandbox.name }}"</vd-dropdown-item>
        </vd-dropdown>
      </VdCard>
    </div>
  </VdPage>
</template>

<script lang="ts">
import { VdInput, VdPage, VdCard } from "@voodoo.io/voodoo-ui";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { defineComponent, computed } from "vue";

import { queryNames, useSandboxListPageQuery, useMutationResponse, useDeleteSandboxMutation, Sandbox } from "@/graphql";
import useMessages from "@/hooks/messages";
import useSearchFilter from "@/hooks/search-filter";
import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";

export default defineComponent({
  name: "SandboxesListPage",

  components: {
    VdIcon,
    VdInput,
    VdPage,
    VdCard,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const { canWriteSandboxes } = usePermissions();

    const { confirm } = useMessages();
    const { mutate: deleteSandbox, loading: deleteLoading } = useDeleteSandboxMutation({
      refetchQueries: [queryNames.SandboxListPage],
    });

    const { result, loading: sandboxLoading } = useSandboxListPageQuery({
      gameId: props.gameId,
    });

    const sandboxes = computed(() => result.value?.game?.sandboxes ?? []);

    const finished = computed(() => !sandboxLoading.value);

    const loading = computed(() => deleteLoading.value || sandboxLoading.value);

    /**
     * Delete a sandbox, but ask before if it's ok :)
     */
    const onSandboxDelete = async (sandbox: Sandbox) => {
      const confirmed = await confirm(`Do you really want to delete the sandbox "${sandbox.name}"?`);
      if (confirmed) {
        const response = await deleteSandbox({ gameId: props.gameId, sandboxId: sandbox.id });
        useMutationResponse(response, (resp) => resp?.deleteSandbox);
      }
    };

    /**
     * Filter the features from the searchTerm
     */
    const { searchTerm, filteredItems: filteredSandboxes } = useSearchFilter(sandboxes, ["name"]);

    const previewSandbox = (id: string) => ({
      name: links.games.sandboxes.display,
      params: {
        gameId: props.gameId,
        sandboxId: id,
      },
    });

    return {
      sandboxes,
      loading,
      finished,
      filteredSandboxes,
      searchTerm,
      previewSandbox,
      onSandboxDelete,
      canWriteSandboxes,
    };
  },
});
</script>

<style lang="scss">
.sandboxes-list-page {
  .sandbox-card {
    margin-bottom: size("ML");

    .vd-card__content {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .left {
        display: flex;
        align-items: center;

        .sandbox-main-info {
          margin-left: size("ML");

          .sandbox-details {
            display: block;
            font-size: font("S");
            color: $grey;
          }
        }
      }
    }
  }
}
</style>
